
import { useState, useEffect, useRef } from 'react';

export const useFanDeck = (activeColorIndex, onIndexChange, colors = [], options = {}) => {
    const { enableWheel = false } = options;
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const lastWheelTime = useRef(0);

    // Track latest values in ref to avoid stale closures in event listeners
    const latestValues = useRef({ activeColorIndex, onIndexChange, startX, colors });

    useEffect(() => {
        latestValues.current = { activeColorIndex, onIndexChange, startX, colors };
    }, [activeColorIndex, onIndexChange, startX, colors]);

    // Wheel Event Listener (Desktop Scroll)
    useEffect(() => {
        if (!enableWheel) return;

        const handleWheel = (e) => {
            const now = Date.now();
            if (now - lastWheelTime.current < 50) return;

            const { activeColorIndex, onIndexChange, colors } = latestValues.current;
            if (Math.abs(e.deltaY) < 10) return;

            if (e.deltaY > 0 && activeColorIndex < colors.length - 1) {
                onIndexChange(activeColorIndex + 1);
                lastWheelTime.current = now;
            } else if (e.deltaY < 0 && activeColorIndex > 0) {
                onIndexChange(activeColorIndex - 1);
                lastWheelTime.current = now;
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: true });
        return () => window.removeEventListener('wheel', handleWheel);
    }, [enableWheel]);

    useEffect(() => {
        if (!isDragging) return;

        const handleMove = (e) => {
            const clientX = e.clientX || (e.touches && e.touches[0].clientX);
            if (!clientX) return;

            const { activeColorIndex, onIndexChange, startX, colors } = latestValues.current;
            const diff = startX - clientX;
            const threshold = 30; // Sensitive threshold

            if (Math.abs(diff) > threshold) {
                if (diff > 0 && activeColorIndex < colors.length - 1) {
                    onIndexChange(activeColorIndex + 1);
                    setStartX(clientX);
                } else if (diff < 0 && activeColorIndex > 0) {
                    onIndexChange(activeColorIndex - 1);
                    setStartX(clientX);
                }
            }
        };

        const handleEnd = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleMove);
        document.addEventListener('touchend', handleEnd);

        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleEnd);
            document.removeEventListener('touchmove', handleMove);
            document.removeEventListener('touchend', handleEnd);
        };
    }, [isDragging]);

    const handleStart = (clientX) => {
        setIsDragging(true);
        setStartX(clientX);
    };

    return {
        containerRef,
        handlers: {
            onMouseDown: (e) => {
                e.preventDefault();
                handleStart(e.clientX);
            },
            onTouchStart: (e) => handleStart(e.touches[0].clientX)
        },
        isDragging
    };
};
